<template>
  <v-dialog v-model="model" width="auto" persistent>
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click.native="close(false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title uppercase>{{$t('AddMedia.AddMedia')}}</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-progress-linear v-if="saveLoading" indeterminate color="primary"></v-progress-linear>
      <v-container>

        <v-radio-group v-model="isManual" class="px-4 pt-3" row>
          <v-radio :label="$t('GeneralTranslations.Manual')" :value="true"></v-radio>
          <v-radio :label="$t('GeneralTranslations.ImportCSV')" :value="false"></v-radio>
        </v-radio-group>

        <div class="p-4" v-if="isManual">
          <validation-observer ref="observer" v-slot>
            <v-form ref="createMediaForm" @submit.prevent="createMedia">
              <v-row class="mt-1">
                <v-col cols="12" md="8">
                  <validation-provider v-slot="{ errors }" name="MediaName" rules="required|max:25">
                    <v-text-field v-model="form.name" :counter="25" :error-messages="errors" :label="$t('AddMedia.MediaName') +'*'">
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="4">
                  <v-switch v-model="form.isEnabled" class :label="$t('GeneralTranslations.Enable')"></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="12" class="py-0">
                  <v-radio-group v-model="isVideo" row>
                    <v-radio :label="$t('AddMedia.Video')" :value="true"></v-radio>
                    <v-radio :label="$t('AddMedia.PDF')" :value="false"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col cols="12" md="3" class="ml-5 upload-file-col">
                  <div class="v-label theme--light pb-5">{{$t('AddMedia.UploadFile')}}*</div>
                  <v-btn v-if="!selectedVideo && !selectedPdf" class="pl-12 file-upload-icon-btn" icon
                    @click="$refs.inputFileUpload.click()">
                    <v-icon class="file-upload-icon">mdi-file-upload-outline</v-icon>
                  </v-btn>
                  <div v-if="isInvalidFile" class="invalid-file">
                    <v-icon>mdi-alert</v-icon>
                    <small class="pl-1"> {{$t('AddMedia.InvalidFileFormat')}}</small>
                  </div>
                  <b v-if="selectedVideo || selectedPdf">
                    <v-icon color="#000">mdi-check-all</v-icon>  {{$t('AddMedia.Loaded')}}
                  </b>
                  <input v-if="isVideo" v-show="false" required type="file" accept="video/mp4,video/x-m4v,video/*"
                    ref="inputFileUpload" @change="onVideoSelect($event)" />
                  <input v-if="!isVideo" v-show="false" required type="file" accept=".pdf" ref="inputFileUpload"
                    @change="onPdfSelect($event)" />
                </v-col>
                <v-col cols="12" md="1" class="pt-12">
                  <div v-if="isVideo" class="v-label theme--light">{{$t('AddMedia.or')}}</div>
                </v-col>
                <v-col cols="12" md="7" class="pb-0 mt-6">
                  <v-text-field v-if="isVideo" :label="$t('AddMedia.AddVideoURL')" :error-messages="errors" v-model="form.videoUrl"
                    v-on:change="onVideoUrlChange" required />
                </v-col>
              </v-row>
              <v-row class="pt-0">
                <v-col cols="12" md="8">
                  <validation-provider v-slot="{ errors }" name="Description" rules="required|max:500">
                    <v-textarea v-model="form.description" :counter="500" :error-messages="errors" rows="2"
                      :label="$t('GeneralTranslations.Description') +'*'" required></v-textarea>
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="img-block">
                    <div class="d-flex justify-content-center align-items-center h-100 image-upload">
                      <img :src="imagePreviewUrl" alt="" class="h-100 w-100" />

                      <div v-if="imgLoading" class="position-absolute top-0 left-0">
                        <v-progress-circular :size="100" color="primary" indeterminate>
                        </v-progress-circular>
                      </div>
                    </div>
                    <div class="float-right">
                      <v-btn icon @click="openGalleryPhotos()">
                        <v-icon class="ignore-readonly" color="#3F51B5">mdi-image-multiple</v-icon>
                      </v-btn>

                      <v-btn icon
                        @click="$refs.inputImgUpload.click()">
                        <v-icon color="#3F51B5">mdi-camera</v-icon>
                      </v-btn>

                      <input v-show="false" ref="inputImgUpload" type="file" accept="image/*" @change="saveImage" />
                    </div>
                  </v-card>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <validation-provider v-slot="{ errors }" name="Categories">
                    <v-select multiple v-model="form.categoryIds" :items="categories" :error-messages="errors"
                      item-text="name" item-value="id" :label="$t('AddMedia.Categories')+'*'"
                      :rules="[(v) => v.length > 0 || $t('AddMedia.YouMustSelectAtLeastOneCategory')]"
                      @change="onCategoryChange">
                    </v-select>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-col cols="12" md="6">
                <div class="mt-3">
                  <h5 class="pb-3"> {{$t('AddMedia.AlsoAddTo')}}:</h5>
                  <v-template v-for="(facility, index) in facilitiesToSelect" :key="index">
                    <v-checkbox :label="facility.name" @change="onFacilityChange(facility, $event)">
                    </v-checkbox>
                  </v-template>
                </div>
              </v-col>
              <v-row class="pb-2">
                <v-col cols="12">
                  <div class="float-right">
                    <v-btn color="primary" type="submit"
                      :disabled="invalid || isInvalidFile || saveLoading || imgLoading || !form.categoryIds.length || (isVideo && !selectedVideo && !form.videoUrl) || (!selectedPdf && !isVideo)"
                      :loading="saveLoading" uppercase>{{$t('AddMedia.CreateMedia')}}</v-btn>
                  </div>
                  <div class="mt-2 v-label theme--light"><small>{{$t('GeneralTranslations.StarIndicatesRequiredField')}}</small></div>
                </v-col>
              </v-row>
            </v-form>
          </validation-observer>
        </div>
        <div v-else class="p-4 pl-3">

          <v-btn class="primary" @click="$refs.selectFileRef.click()">
                {{$t('GeneralTranslations.SelectFile')}}
          </v-btn>
          <span class="pl-4">{{filename}}</span>

          <input v-show="false" ref="selectFileRef" type="file" accept="text/csv" @change="onLoadFile" />

          <div class="pt-4">
          <a href="https://firebasestorage.googleapis.com/v0/b/reshub-test.appspot.com/o/csv-templates%2FTemplate%20content%20upload%20csv%202.0.csv?alt=media&token=3b492e92-25b5-41c3-9b00-e670daa3840b"
            download="download"> {{$t('GeneralTranslations.DownloadCSVTemplate')}}</a>
          </div>

          <div class="error--text v-messages__message pt-2" v-if="isError">{{ errorContent }}</div>

          <div v-if="csv">
            <div class="v-data-table v-data-table--has-bottom theme--light">
              <div class="v-data-table__wrapper">
                <table class="preview-table" v-if="csv">
                  <thead>
                    <tr>
                      <th v-for="(header, key) in csv.meta.fields" :key="'header-' + key">
                        <span v-if="header === 'Enabled'">
                          <div class="d-flex">
                            <div class="mt-1 pr-2">
                              {{ header }}
                            </div>
                            <div class="right cursor-pointer">
                              <v-icon color="secondary"
                                :title="$t('AddMedia.DoYouWantThisToDisplayOnMyHubMessage')">
                                mdi-information</v-icon>
                            </div>
                          </div>
                        </span>
                        <span v-else>{{ header }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowKey) in csv.data.slice(0, 5)" :key="'row-' + rowKey">
                      <td v-for="(column, columnKey) in csv.meta.fields" :key="'row-' + rowKey + '-column-' + columnKey">
                        <span :title="csv.data[rowKey][column]">
                          {{ csv.data[rowKey][column] }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <v-row class="pl-3 pt-3">
            <v-col class="pl-0 pt-5" cols="12" md="6">
              <h5 class="pl-1 pb-3">{{$t('AddMedia.AlsoAddTo')}}:</h5>
              <div class="pl-1">
                <v-template v-for="(facility, index) in facilitiesToSelect" :key="index">
                  <v-checkbox :label="facility.name" @change="onFacilityChange(facility, $event)">
                  </v-checkbox>
                </v-template>
              </div>
            </v-col>
          </v-row>

          <v-row class="pb-2">
            <v-col cols="12">
              <div class="float-right">
                <v-btn color="primary" @click="importCsvData" :disabled="!csvData" :loading="importLoading" uppercase> {{$t('AddMedia.ImportMedia')}}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>

      <GalleryPhotosUploadDlg ref="GalleryPhotosUploadDlg" @uploadGalleryPhoto="uploadGalleryPhoto" />
    </v-card>
  </v-dialog>
</template>

<script>
import * as firebase from "firebase/app";
import { uploadImage, uploadImageAsync, deleteFileByPath, optimizePhotos } from "@/utils/upload-image";
import { v4 as uuidv4 } from "uuid";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapState } from "vuex";
import { serializer } from '../../api/shared/serializer';
import { ResidentStatus } from "@/enums/ResidentStatus";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {waitResizedImage} from "@/utils/wait-resized-image";

export default {
  components: { ValidationObserver, ValidationProvider, GalleryPhotosUploadDlg },
  data() {
    return {
      imagePreviewUrl: require('@/assets/no-image.jpg'),
      isVideo: true,
      selectedVideo: null,
      selectedPdf: null,
      selectedImage: null,
      isInvalidFile: false,
      facilitiesToSelect: [],
      selectedFacilitiesIds: [],
      importLoading: false,
      csv: null,
      csvData: null,
      csvFileName: "",
      filename: null,
      errorContent: "",
      isError: false,
      isManual: true,
      form: {
        name: null,
        categoryId: null,
        videoUrl: null,
        imageUrl: null,
        thumbnailUrl: null,
        isEnabled: true,
        description: null,
        categoryIds: [],
        categories: []
      },
      saveLoading: false,
      imgLoading: false,
      csvHeaders: {
        name: 'name',
        description: 'description',
        firstCategory: 'first category',
        secondCategory: 'second category',
        thirdCategory: 'third category',
        imageUrl: 'image url',
        youtubeUrl: 'youtube url',
        enabled: 'enabled'
      },
      residentStatus: ResidentStatus
    };
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.authModule.user,
      currentFacility: state => state.authModule.currentFacility,
      categories: (state) => state.contentLibraryModule.contentCategories,
      facilities: (state) => state.facilityModule.facilities
    }),
    model: {
      get() {
        return this.value;
      },
      set() {
        this.close(false)
      }
    }
  },
  created() {
    this.$store.dispatch('getContentCategories')
    this.$store.dispatch("getFacilities")
  },
  watch: {
    facilities(list) {
      if (list) {
        this.facilitiesToSelect = list.filter(x => x.id != this.currentFacility)
      }
    }
  },
  methods: {
    onLoadFile(e) {
      if (!e || !e.currentTarget || !this.$papa || !this.$papa.parse) return

      const file = e.currentTarget.files[0]

      this.filename = file.name

      this.$papa.parse(file, {
        header: true,
        complete: this.onComplete,
        skipEmptyLines: true,
      })
    },

    onComplete(results, file) {
      this.clearError()

      if (file.type !== "text/csv" && file.type !== "application/vnd.ms-excel") {
        const message = this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.PleaseUploadaCSVFile']
        this.setError(message)
        return false;
      }

      if (!this.isCsvDataValid(results)) {
        const message = this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.PleaseUploadaCSVFileByFollowingFormat'] +"Name,Description,First category,Second category (optional),Third category (optional),Image URL (optional),YouTube URL (optional),Enabled";
        this.setError(message)
        return false;
      }

      this.csv = results;
      this.csvData = results.data
      this.csvFileName = file.name
    },

    isCsvDataValid(results) {

      const isHeadersValid = (results.meta.fields.filter(f => f.toLowerCase().includes(this.csvHeaders.name)
        || f.toLowerCase().includes(this.csvHeaders.description)
        || f.toLowerCase().includes(this.csvHeaders.firstCategory)
        || f.toLowerCase().includes(this.csvHeaders.secondCategory)
        || f.toLowerCase().includes(this.csvHeaders.thirdCategory)
        || f.toLowerCase().includes(this.csvHeaders.imageUrl)
        || f.toLowerCase().includes(this.csvHeaders.youtubeUrl)
        || f.toLowerCase().includes(this.csvHeaders.enabled)).length === results.meta.fields.length)

      if (!isHeadersValid) return false

      const isMandatoryFieldsPopulated = !results.data.some((raw) =>
        !this.getRawValues(raw).firstCategory
        || !this.getRawValues(raw).name
        || !this.getRawValues(raw).description
        || this.getRawValues(raw).enabled === undefined
      )

      if (!isMandatoryFieldsPopulated) return false

      return true
    },

    clearError() {
      this.isError = false
      this.errorContent = ""
    },

    getRawValues(raw) {

      const data = {
        name: '',
        description: '',
        firstCategory: '',
        secondCategory: '',
        thirdCategory: '',
        imageUrl: '',
        youtubeUrl: '',
        enabled: undefined
      }

      for (const key of Object.keys(raw)) {

        const value = raw[key] && typeof raw[key] === 'string' ? raw[key].trim() : raw[key]

        if (key.toLowerCase().includes(this.csvHeaders.name)) {
          data.name = value
        } else if (key.toLowerCase().includes(this.csvHeaders.description)) {
          data.description = value
        } else if (key.toLowerCase().includes(this.csvHeaders.firstCategory)) {
          data.firstCategory = value
        } else if (key.toLowerCase().includes(this.csvHeaders.secondCategory)) {
          data.secondCategory = value
        } else if (key.toLowerCase().includes(this.csvHeaders.thirdCategory)) {
          data.thirdCategory = value
        } else if (key.toLowerCase().includes(this.csvHeaders.imageUrl)) {
          data.imageUrl = value
        } else if (key.toLowerCase().includes(this.csvHeaders.youtubeUrl)) {
          data.youtubeUrl = value
        } else if (key.toLowerCase().includes(this.csvHeaders.enabled)) {
          data.enabled = (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') ? (value.toLowerCase() === 'true') : undefined
        }
      }

      return data
    },

    async importCsvData() {
      this.importLoading = true

      const selectedFacilitiesIds = this.selectedFacilitiesIds.concat([this.currentFacility])

      for (const facilityId of selectedFacilitiesIds) {

        const medias = []
        const categories = []
        for (const raw of this.csvData) {

          const firstCategory = this.getRawValues(raw).firstCategory
          const secondCategory = this.getRawValues(raw).secondCategory
          const thirdCategory = this.getRawValues(raw).thirdCategory
          this.form.facilityId = facilityId
          this.form.name = this.getRawValues(raw).name
          this.form.description = this.getRawValues(raw).description
          this.form.imageUrl = this.getRawValues(raw).imageUrl
          this.form.videoUrl = this.getRawValues(raw).youtubeUrl
          this.form.isEnabled = this.getRawValues(raw).enabled
          this.form.isFromCsv = true
          let createdMedia = this.form
          createdMedia.csvFileName = this.csvFileName

          await this.$store.dispatch('addContent', createdMedia).then(async (response) => {
            const mediaId = response.documentId
            createdMedia = serializer.documentSnapshotToDocument(await firebase.firestore().collection("contentLibrary").doc(mediaId).get())
            medias.push(createdMedia)

            const category1 = await this.$store.dispatch('getContentCategoryByFacility', { facilityId: facilityId, name: firstCategory })
            
            if (category1) {
              createdMedia.categories = [{ id: category1.id, name: category1.name }]
              createdMedia.categoryIds = [category1.id]
            }
            else {
              const newCategory1 = {
                name: firstCategory,
                facilityId: facilityId,
                createdByStaffId: createdMedia.createdByStaffId,
                createdByStaffName: createdMedia.createdByStaffName,
                dateTimeCreated: createdMedia.dateTimeCreated,
                csvFileName: this.csvFileName
              }
              const response = await this.$store.dispatch('addContentCategory', newCategory1)
              createdMedia.categories = [{ id: response.documentId, name: firstCategory }]
              createdMedia.categoryIds = [response.documentId]

              newCategory1.id = response.documentId
              categories.push(newCategory1)
            }

            if (secondCategory.length) {
              const category2 = await this.$store.dispatch('getContentCategoryByFacility', { facilityId: facilityId, name: secondCategory })
              if (category2) {
                createdMedia.categories = createdMedia.categories.concat([{ id: category2.id, name: category2.name }])
                createdMedia.categoryIds = createdMedia.categoryIds.concat([category2.id])
              }
              else {
                const newCategory2 = {
                  name: secondCategory,
                  facilityId: facilityId,
                  createdByStaffId: createdMedia.createdByStaffId,
                  createdByStaffName: createdMedia.createdByStaffName,
                  dateTimeCreated: createdMedia.dateTimeCreated,
                  csvFileName: this.csvFileName
                }
                const response = await this.$store.dispatch('addContentCategory', newCategory2)
                createdMedia.categories = createdMedia.categories.concat([{ id: response.documentId, name: secondCategory }])
                createdMedia.categoryIds = createdMedia.categoryIds.concat([response.documentId])

                newCategory2.id = response.documentId
                categories.push(newCategory2)
              }
            }

            if (thirdCategory.length) {
              const category3 = await this.$store.dispatch('getContentCategoryByFacility', { facilityId: facilityId, name: thirdCategory })

              if (category3) {
                createdMedia.categories = createdMedia.categories.concat([{ id: category3.id, name: category3.name }])
                createdMedia.categoryIds = createdMedia.categoryIds.concat([category3.id])
              }
              else {
                const newCategory3 = {
                  name: thirdCategory,
                  facilityId: facilityId,
                  createdByStaffId: createdMedia.createdByStaffId,
                  createdByStaffName: createdMedia.createdByStaffName,
                  dateTimeCreated: createdMedia.dateTimeCreated,
                  csvFileName: this.csvFileName
                }
                const response = await this.$store.dispatch('addContentCategory', newCategory3)
                createdMedia.categories = createdMedia.categories.concat([{ id: response.documentId, name: thirdCategory }])
                createdMedia.categoryIds = createdMedia.categoryIds.concat([response.documentId])

                newCategory3.id = response.documentId
                categories.push(newCategory3)
              }
            }

            await firebase.firestore().collection("contentLibrary").doc(mediaId).update({
              categories: createdMedia.categories,
              categoryIds: createdMedia.categoryIds
            })

          }).catch(e => {
            console.error(`Unabled to create media for facility ${facilityId}`, e)
          });

          console.log('raw: ' + createdMedia.name + ' ; facilityId: ' + facilityId)
        }
      }

      this.importLoading = false;
      this.csv = null;
      this.csvData = null;
      this.close(true);
    },

    setError(message) {
      this.isError = true;
      this.errorContent = message;
      this.filename = null;
      this.csv = null;
      this.$refs.input.focusout;
    },

    onFacilityChange(facility, isSelected) {
      if (isSelected) {
        this.selectedFacilitiesIds.push(facility.id)
      }
      else {
        this.selectedFacilitiesIds.splice(this.selectedFacilitiesIds.indexOf(facility.id), 1)
      }
    },

    onCategoryChange(category) {
      this.form.categories = this.categories.filter(x => this.form.categoryIds.includes(x.id)).map(x => ({ id: x.id, name: x.name })) || []
    },

    onVideoSelect(event) {
      this.isInvalidFile = !['m4v', 'avi', 'mpg', 'mp4'].includes(this.getExtension(event.target.files[0].name).toLowerCase())
      if (!this.isInvalidFile) {
        this.selectedPdf = null
        this.selectedVideo = event.target.files[0]
        this.form.videoUrl = null
      }
    },

    onPdfSelect(event) {
      this.isInvalidFile = this.getExtension(event.target.files[0].name).toLowerCase() != 'pdf'
      if (!this.isInvalidFile) {
        this.selectedVideo = null
        this.selectedPdf = event.target.files[0]
      }
    },

    onVideoUrlChange(value) {
      if (this.isVideo && !this.selectedVideo && value) {
        this.form.videoUrl = decodeURIComponent(value)
      }

      if (this.selectedVideo) {
        this.selectedVideo = null
        this.$refs.inputFileUpload.value = null;
      }
    },

    async createMedia() {
      const isValid = await this.$refs.observer.validate()

      if (!isValid) return

      this.saveLoading = true

      const selectedFacilitiesIds = this.selectedFacilitiesIds.concat([this.currentFacility])

      for (const facilityId of selectedFacilitiesIds) {

        this.form.facilityId = facilityId
        await this.$store.dispatch('addContent', this.form).then(async (response) => {
          const mediaId = response.documentId
          const media = serializer.documentSnapshotToDocument(await firebase.firestore().collection("contentLibrary").doc(mediaId).get())

          const promises = [];
          if (this.selectedVideo) {
            const videoPromise = this.uploadFile(this.selectedVideo, mediaId, facilityId);
            promises.push(videoPromise);
          }

          if (this.selectedPdf) {
            const pdfPromise = this.uploadFile(this.selectedPdf, mediaId, facilityId);
            promises.push(pdfPromise);
          }

          if (this.selectedImage && this.selectedImage.file) {
            const imagePromise = await this.uploadContentImage(this.selectedImage.file, mediaId, facilityId);
            promises.push(imagePromise);
          }
        })
        .catch(e => {
          console.error(`Unabled to create media for facility ${facilityId}`, e);
        });

      }
      this.saveLoading = false;

      this.close(true);
    },

    async uploadFile(file, contentId, facilityId) {
      if (!file) return;

      try {
        const storageRef = firebase.storage().ref()
        const filesRef = storageRef.child(facilityId).child('contentLibrary')

        let type = file.extension || file.type;
        if (type === 'pdf') {
          type = file.type;
        }

        const uploadFileRef = filesRef.child(contentId).child(`${file.name}`)

        return await uploadFileRef.put(file, { contentType: type }).then(async () => {
          return await uploadFileRef.getDownloadURL().then(async (url) => {
            const update = type.includes('pdf') ? { pdfUrl: url } : { videoUrl: url }
            return await firebase.firestore().collection("contentLibrary").doc(contentId).update(update)
          })
        })

      } catch (error) {
        this.saveLoading = true
        return;
      }
    },

    async saveImage(e) {
      const file = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputImgUpload.value = null;
        return;
      }

      this.selectedImage = { file: file, path: '', preloadPath: '' }
      this.preloadContentImage(this.selectedImage.file, this.currentFacility)
    },

    async preloadContentImage(file, facilityId) {
      const path = `${facilityId}/contentLibrary/tmp/${uuidv4()}`;
      this.selectedImage.preloadPath = path
      uploadImage({
        path, file,
        onStart: () => {
          this.imgLoading = true;
        },
        onComplete: async storage => {
          this.imagePreviewUrl = await storage.snapshot.ref.getDownloadURL();
          this.imgLoading = false;
          console.log(`image preloaded: ${this.imagePreviewUrl}`)
        }
      });
    },

    async uploadContentImage(file, contentId, facilityId) {
      const path = `${facilityId}/contentLibrary/${contentId}/${uuidv4()}`;
      this.selectedImage.path = path
      return await uploadImageAsync({
        path,
        file
      }).then(async (storage) => {
        const uploadedImageUrl = await storage.ref.getDownloadURL();
        const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

        try {
          const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
          this.form.imageUrl = uploadedImageUrl
          this.form.thumbnailUrl = resizedImageUrl
        } catch (e) {
          this.form.imageUrl = uploadedImageUrl;
          this.form.thumbnailUrl = uploadedImageUrl;
        }

        await firebase.firestore().collection("contentLibrary").doc(contentId).update({
          imageUrl: this.form.imageUrl,
          thumbnailUrl: this.form.thumbnailUrl
        }).then(async () => {
          console.log(`image uploaded: ${this.form.imageUrl}`)
          const preloadPath = this.selectedImage?.preloadPath
          if (preloadPath) {
            await deleteFileByPath(`${preloadPath}`)
            await deleteFileByPath(`${preloadPath}_200x200`)
            this.selectedImage.preloadPath = ''
          }
        })
      });
    },

    getExtension(filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },

    async close(contentCreated) {
      if (contentCreated) {
        await this.$store.dispatch("getContentItems")
      }
      else if (this.selectedImage) {
        const preloadPath = this.selectedImage.preloadPath
        if (preloadPath) {
          await deleteFileByPath(this.selectedImage.preloadPath)
          await deleteFileByPath(`${this.selectedImage.preloadPath}_200x200`)
          this.selectedImage.preloadPath = ''
        }
      }
      this.isVideo = true
      this.selectedVideo = null
      this.selectedPdf = null
      this.selectedImage = null
      this.form.name = null
      this.form.description = null
      this.form.videoUrl = null
      this.form.imageUrl = null
      this.form.isEnabled = true
      this.form.pdfUrl = null
      this.form.categoryIds = []
      this.form.categories = []
      this.form.categoryId = null
      this.form.categoryName = null
      this.saveLoading = false
      this.imgLoading = false
      this.imagePath = null
      this.filename = null
      this.csv = null
      this.csvData = null
      this.selectedFacilitiesIds = []
      this.imagePreviewUrl = require('@/assets/no-image.jpg')
      if(this.$refs.inputFileUpload?.value) {
        this.$refs.inputFileUpload.value = null;
      }
      console.log('close: ' + contentCreated)
      this.$emit("close")
    },

    openGalleryPhotos() {
      this.$refs.GalleryPhotosUploadDlg.open();
    },

    uploadGalleryPhoto(image) {
      this.form.imageUrl = image.image;
      this.imagePreviewUrl = image.image;
      this.form.thumbnailUrl = image.thumbnailUrl ?? image.image;
    },
  },
};
</script>

<style scoped>
.preview-table {
  width: 100%;
  margin-top: 10px
}

.file-upload-icon {
  color: #3F51B5 !important;
  font-size: 50px !important;
}

::v-deep .v-dialog {
  min-width: 900px;
  max-width: 1300px;
}

::v-deep .file-upload-icon-btn:before {
  opacity: 0 !important;
}

::v-deep .v-ripple__container {
  opacity: 0 !important;
}

.upload-button-position {
  bottom: 13px;
  right: 13px;
}

.image-upload {
  min-height: 230px;
}

.img-block {
  height: 230px !important;
  width: 230px !important;
}

.v-card__title,
.v-icon {
  color: white;
}

img {
  border-radius: 4px;
}

.invalid-file {
  padding-top: 5px;
  color: red;
}

::v-deep .invalid-file>.v-icon {
  color: red !important;
  font-size: 14px !important;
}

::v-deep .v-file-input__text {
  width: 200px;
}

.upload-file-col {
  max-width: 150px !important;
}

.v-window-item,
.v-window__container {
  background-color: #fff !important;
}
</style>
